import React from 'react';
import { calculateRisk } from '../../utils/helpers';

function RiskHeatmap({ risks }) {
  // Initialize grid for all P×I combinations
  const grid = {};
  for (let p = 1; p <= 5; p++) {
    for (let i = 1; i <= 5; i++) {
      grid[`${p}_${i}`] = [];
    }
  }

  // Populate grid with risks
  risks.forEach((r) => {
    const key = `${r.p}_${r.i}`;
    if (grid[key]) grid[key].push(r);
  });

  const cellColor = (p, i) => {
    const product = p * i;
    if (product <= 4) return '#9fe1cb';
    if (product <= 10) return '#c0dd97';
    if (product <= 16) return '#fac775';
    if (product <= 20) return '#ef9f27';
    if (product <= 25) return '#e24b4a';
    return '#a32d2d';
  };

  const getRiskColor = (r) => {
    const calc = calculateRisk(r.p, r.i, r.c);
    if (r.status === 'mitigated') return 'var(--ok)';
    if (calc.band === 'critical') return 'var(--danger)';
    if (calc.band === 'high') return 'var(--warn)';
    if (calc.band === 'medium') return 'var(--teal)';
    return 'var(--accent-3)';
  };

  const probabilityValues = [5, 3, 1];
  const probabilityLabels = ['High', 'Medium', 'Low'];
  const impactLabels = ['Low', 'Med-Low', 'Medium', 'Med-High', 'High'];

  return (
    <div className="card card-pad">
      {/* Impact header */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '60px repeat(5, 1fr)', 
        gap: 4,
        marginBottom: 4
      }}>
        <div></div>
        {impactLabels.map((label, i) => (
          <div key={i} className="xs muted" style={{ 
            textAlign: 'center', 
            fontWeight: 500, 
            letterSpacing: '0.06em',
            padding: '4px 0'
          }}>
            {label}
          </div>
        ))}
      </div>

      {/* Grid rows */}
      {probabilityValues.map((p, rowIdx) => {
        const actualP = p;
        
        return (
          <div key={rowIdx} style={{ 
            display: 'grid', 
            gridTemplateColumns: '60px repeat(5, 1fr)', 
            gap: 4,
            marginBottom: 4
          }}>
            <div className="xs muted" style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'flex-end', 
              paddingRight: 10,
              fontWeight: 500,
              letterSpacing: '0.06em'
            }}>
              {probabilityLabels[rowIdx]}
            </div>
            {[1, 2, 3, 4, 5].map((i) => {
              const key = `${actualP}_${i}`;
              const cells = grid[key] || [];
              const hasRisks = cells.length > 0;
              const baseColor = cellColor(actualP, i);
              
              return (
                <div
                  key={i}
                  style={{
                    background: baseColor,
                    border: `1px solid var(--line)`,
                    minHeight: 56,
                    padding: 4,
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignContent: 'flex-start',
                    gap: 3,
                    borderRadius: 2,
                    opacity: hasRisks ? 1 : 0.3
                  }}
                >
                  {cells.map((r) => {
                    const calc = calculateRisk(r.p, r.i, r.c);
                    return (
                      <div
                        key={r.id}
                        className="mono"
                        style={{
                          background: getRiskColor(r),
                          color: 'white',
                          padding: '1px 5px',
                          fontSize: 9,
                          borderRadius: 2,
                          cursor: 'pointer',
                          fontWeight: r.status === 'mitigated' ? 400 : 600,
                          opacity: r.status === 'closed' ? 0.4 : 1
                        }}
                        title={`${r.code} — ${r.title}\nScore: ${calc.score} (${calc.label})\nStatus: ${r.status}`}
                      >
                        {r.code}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        );
      })}

      {/* Impact footer */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '60px repeat(5, 1fr)', 
        gap: 4,
        marginTop: 4
      }}>
        <div></div>
        <div className="xs muted" style={{ 
          gridColumn: 'span 5', 
          textAlign: 'center',
          fontWeight: 500,
          letterSpacing: '0.06em',
          padding: '6px 0'
        }}>
          <span className="mono" style={{ textTransform: 'uppercase', letterSpacing: '0.12em' }}>IMPACT →</span>
        </div>
      </div>

      {/* Legend */}
      <div style={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: '12px 20px', 
        paddingTop: 14, 
        marginTop: 12, 
        borderTop: '1px solid var(--line)',
        fontSize: 11,
        color: 'var(--ink-3)'
      }}>
        <div className="flex gap-2 center">
          <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 3, background: '#9fe1cb', border: '1px solid var(--line)' }}></span>
          Low
        </div>
        <div className="flex gap-2 center">
          <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 3, background: '#c0dd97', border: '1px solid var(--line)' }}></span>
          Med-Low
        </div>
        <div className="flex gap-2 center">
          <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 3, background: '#fac775', border: '1px solid var(--line)' }}></span>
          Medium
        </div>
        <div className="flex gap-2 center">
          <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 3, background: '#ef9f27', border: '1px solid var(--line)' }}></span>
          Med-High
        </div>
        <div className="flex gap-2 center">
          <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 3, background: '#e24b4a', border: '1px solid var(--line)' }}></span>
          High
        </div>
        <div className="flex gap-2 center">
          <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 3, background: '#a32d2d', border: '1px solid var(--line)' }}></span>
          Critical
        </div>
        <div className="mono xs muted" style={{ marginLeft: 'auto' }}>
          Score = P × I × Control · Bands: Low ≤20, Medium ≤50, High ≤80, Critical &gt;80
        </div>
      </div>
    </div>
  );
}

export default RiskHeatmap;